import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { Purse } from '@clicklinx/protocol'
import { api, loadStoredTokens, setTokens, type Tokens } from './api'

export interface CurrentUser {
  id: string
  email: string
  displayName: string
  /** Practice coins. Free, never redeemable. */
  balance: number
  /** Prize coins, backed one-for-one by cents. */
  prizeCoins: number
}

interface AuthContextValue {
  user: CurrentUser | null
  /** True only while the stored session is being restored on first paint. */
  initialising: boolean
  login: (email: string, password: string) => Promise<void>
  register: (
    email: string,
    password: string,
    displayName: string,
  ) => Promise<{ needsConfirmation: boolean }>
  confirm: (email: string, code: string) => Promise<void>
  resendCode: (email: string) => Promise<void>
  logout: () => void
  setBalance: (purse: Purse, balance: number) => void
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

interface AuthResponse {
  user: CurrentUser | null
  tokens: Tokens | null
  needsConfirmation?: boolean
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null)
  const [initialising, setInitialising] = useState(true)

  const refreshProfile = useCallback(async () => {
    const result = await api<{ user: CurrentUser }>('/me', { auth: true })
    setUser(result.user)
  }, [])

  // Restore a stored session before the first render decides what to show, so
  // a returning player never sees the sign-in screen flash past.
  useEffect(() => {
    const stored = loadStoredTokens()
    if (!stored) {
      setInitialising(false)
      return
    }
    refreshProfile()
      .catch(() => {
        setTokens(null)
        setUser(null)
      })
      .finally(() => setInitialising(false))
  }, [refreshProfile])

  const login = useCallback(async (email: string, password: string) => {
    const result = await api<AuthResponse>('/auth/login', {
      method: 'POST',
      body: { email, password },
    })
    if (result.tokens) setTokens(result.tokens)
    if (result.user) setUser(result.user)
  }, [])

  const register = useCallback(
    async (email: string, password: string, displayName: string) => {
      const result = await api<AuthResponse>('/auth/register', {
        method: 'POST',
        body: { email, password, displayName: displayName || undefined },
      })
      // The local provider signs you straight in; Cognito emails a code first.
      if (result.tokens) setTokens(result.tokens)
      if (result.user) setUser(result.user)
      return { needsConfirmation: Boolean(result.needsConfirmation) }
    },
    [],
  )

  const confirm = useCallback(async (email: string, code: string) => {
    await api('/auth/confirm', { method: 'POST', body: { email, code } })
  }, [])

  const resendCode = useCallback(async (email: string) => {
    await api('/auth/resend', { method: 'POST', body: { email } })
  }, [])

  const logout = useCallback(() => {
    setTokens(null)
    setUser(null)
  }, [])

  /** Updates one purse without disturbing the other. */
  const setBalance = useCallback((purse: Purse, balance: number) => {
    setUser((current) => {
      if (!current) return current
      return purse === 'prize' ? { ...current, prizeCoins: balance } : { ...current, balance }
    })
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      initialising,
      login,
      register,
      confirm,
      resendCode,
      logout,
      setBalance,
      refreshProfile,
    }),
    [user, initialising, login, register, confirm, resendCode, logout, setBalance, refreshProfile],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>')
  return context
}
